import React, { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import Input from '../../../../components/forms/Input'
import ParameterAction from '../actions/ParameterAction'
import ParameterGroupDto from '../dto/ParameterGroupDto'
import ParameterGroupLinkDto from '../dto/ParameterGroupLinkDto'
import ProgressCard from '../../../../components/card/ProgressCard'
import ParameterDto from '../dto/ParameterDto'
import { PATH_REFERENCIAL, PATH_REFERENCIAL_PARAMETER, PATH_REFERENCIAL_PARAMETERS_GROUP_NEW } from 'home/constants/RouteConstants'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { checkMandatoryFields } from 'utils/FormUtils'
import { push } from '@lagunovsky/redux-react-router'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import SelectParameter from 'campaign/components/campaignDashboard/quality/SelectParameter'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Textarea from 'components/forms/Textarea'
import ReferencialAction from 'referencial/action/ReferencialAction'
import Select from 'components/forms/Select'
import ToastrAction from 'toastr/actions/ToastrAction'
import { hasValue } from 'utils/NumberUtil'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import Checkbox from 'components/forms/Checkbox'
import WaitAction from 'wait/WaitAction'
import { useParams } from 'react-router'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'

const PARAMETER_GROUP_HEADERS = [
    'id',
    'name',
    'definition',
]

const PARAMETER_GROUP_HEADERS_LINKS = [
    'id',
    'name',
    'definition',
    'classGroupCode',
    'usageGroupCode1',
    'usageGroupCode2',
    'usageGroupCode3',
    'usageGroupCode4',
    'usageGroupCode5',
]

const ParameterGroupApp = () => {
    const { code } = useParams()
    const {
        parameterGroupLinks,
        parameterGroups,
        parameters,
        referencialStatus,
        accountUser,
        contacts,
    } = useSelector(store => ({
        parameterGroupLinks: store.ParameterReducer.parameterGroupLinks,
        parameterGroups: store.ParameterReducer.parameterGroups,
        parameters: store.ParameterReducer.parameters,
        referencialStatus: store.ReferencialReducer.referencialStatus,
        accountUser: store.AccountReducer.accountUser,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const isNew = code === 'new'

    const [group, setGroup] = useState({})

    const [classParameter, setClassParameter] = useState([])
    const [usageParameter, setUsageParameter] = useState([])
    const [tempClassParameter, setTempClassParameter] = useState([])
    const [tempUsageParameter, setTempUsageParameter] = useState([])
    const [selectClassParameter, setSelectClassParameter] = useState(false)
    const [selectUsageParameter, setSelectUsageParameter] = useState(false)

    const [editMode, setEditMode] = useState(isNew)
    const [isClassGroup, setIsClassGroup] = useState(isNew)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!parameterGroups.length) {
            dispatch(ParameterAction.fetchParameterGroups())
        }
        if (!parameterGroupLinks.length) {
            dispatch(ParameterAction.fetchParameterGroupLinks())
        }
        if (!parameters.length) {
            dispatch(ParameterAction.fetchParameters())
        }
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const promises = !parameterGroups.length ? [ParameterAction.fetchParameterGroups] : []
        const promisesGroupLinks = !parameterGroupLinks.length ? [...promises, ParameterAction.fetchParameterGroupLinks] : promises
        const promisesParameters = !parameters.length ? [...promisesGroupLinks, ParameterAction.fetchParameters] : promisesGroupLinks
        const promisesStatus = !referencialStatus.length ? [...promisesParameters, ReferencialAction.fetchReferencialStatus] : promisesParameters
        return promisesStatus.map(p => dispatch(p()))
    }, [])

    useTitle(() => {
        if (isNew) {
            return [
                {
                    title: i18n.referencials,
                    href: PATH_REFERENCIAL,
                },
                {
                    title: i18n.parameters,
                    href: PATH_REFERENCIAL_PARAMETER,
                },
                {
                    title: i18n.new,
                    href: PATH_REFERENCIAL_PARAMETERS_GROUP_NEW,
                },
            ]
        }
        return [
            {
                title: i18n.referencials,
                href: PATH_REFERENCIAL,
            },
            {
                title: i18n.parameters,
                href: PATH_REFERENCIAL_PARAMETER,
            },
            {
                title: parameterGroups.find(p => p.code === parseInt(code))?.name || '',
                href: `referencial/parameterGroup/${code}`,
            },
        ]
    }, [code, parameterGroups])

    const defaultParameterGroup = useMemo(() => parameterGroups.find(param => param.code === parseInt(code)) || {}, [code, parameterGroups])

    useEffect(() => {
        if (code === 'new') {
            setGroup({ name: i18n.newGroup })
        } else {
            setGroup(defaultParameterGroup)
        }
    }, [code, defaultParameterGroup])

    const setGroupParameters = () => {
        const parametersClassGroups = parameterGroupLinks.filter(groupLinks => (groupLinks.classGroupCode && parseInt(code) === groupLinks.classGroupCode))
        const parametersUsageGroups = parameterGroupLinks.filter(groupLinks =>
            (parseInt(code) === groupLinks?.usageGroupCode) ||
            (parseInt(code) === groupLinks?.usageGroupCode2) ||
            (parseInt(code) === groupLinks?.usageGroupCode3) ||
            (parseInt(code) === groupLinks?.usageGroupCode4) ||
            (parseInt(code) === groupLinks?.usageGroupCode5),
        )
        const classParameters = parameters.filter(param => parametersClassGroups.find(pg => pg.parameterCode === param.code))
        const usageParameters = parameters.filter(param => parametersUsageGroups.find(pg => pg.parameterCode === param.code))

        setClassParameter(classParameters.map(p => {
            const parametersClassGroup = parametersClassGroups.find(pg => pg.parameterCode === p.code)

            return {
                ...p,
                id: p.code,
                classGroupCode: parametersClassGroup?.classGroupCode,
                usageGroupCode1: parametersClassGroup?.usageGroupCode,
                usageGroupCode2: parametersClassGroup?.usageGroupCode2,
                usageGroupCode3: parametersClassGroup?.usageGroupCode3,
                usageGroupCode4: parametersClassGroup?.usageGroupCode4,
                usageGroupCode5: parametersClassGroup?.usageGroupCode5,
            }
        }))
        setTempClassParameter(classParameters.map(cp => ({ parameterCode: cp.code, fractionCode: cp.fractionCode, placeCode: cp.placeCode, supportCode: cp.supportCode, unitCode: cp.unitCode })))
        setUsageParameter(usageParameters.map(p => {
            const parametersUsageGroup = parametersUsageGroups.find(pg => pg.parameterCode === p.code)

            return {
                ...p,
                id: p.code,
                classGroupCode: parametersUsageGroup?.classGroupCode,
                usageGroupCode1: parametersUsageGroup?.usageGroupCode,
                usageGroupCode2: parametersUsageGroup?.usageGroupCode2,
                usageGroupCode3: parametersUsageGroup?.usageGroupCode3,
                usageGroupCode4: parametersUsageGroup?.usageGroupCode4,
                usageGroupCode5: parametersUsageGroup?.usageGroupCode5,
            }
        }))
        setTempUsageParameter(usageParameters.map(cp => ({ parameterCode: cp.code, fractionCode: cp.fractionCode, placeCode: cp.placeCode, supportCode: cp.supportCode, unitCode: cp.unitCode })))
    }

    const mandatoryFileds = useMemo(() => [
        { field: 'name', i18n: 'name' },
        { field: 'status', i18n: 'status' },
    ], [])

    const checkedClassGroup = useMemo(() => {
        return !isNew
            ? parameterGroupLinks.some(link =>
                link.classGroupCode === parseInt(code))
            : isClassGroup
    }, [isClassGroup, isNew, parameterGroupLinks, code])

    useActions(() => {
        if (isNew) {
            return {
                save: () => {
                    checkMandatoryFields(mandatoryFileds, group, () => {
                        if ((checkedClassGroup && !classParameter.length) || (!checkedClassGroup && !usageParameter.length)) {
                            dispatch(ToastrAction.warning(i18n.pleaseEnterAtLeastOneParameter))
                        } else {
                            dispatch(WaitAction.waitStart())
                            dispatch(ParameterAction.createParameterGroup({
                                parameterGroup: { ...group, status: hasValue(group.status) && `${group.status}` },
                                classParameterIds: classParameter.map(({ id }) => id),
                                usageParameterIds: usageParameter.map(({ id }) => id),
                            })).then(newCode => {
                                dispatch(WaitAction.waitStop())
                                dispatch(push(`/referencial/parameterGroup/${newCode}`))
                            })
                        }
                    })
                },
                cancel: () => {
                    dispatch(push(PATH_REFERENCIAL_PARAMETER))
                },
            }
        }
        if (editMode) {
            return {
                delete: () => {
                    dispatch(WaitAction.waitStart())
                    dispatch(ParameterAction.deleteParameterGroupWithLink(code)).then(() => {
                        dispatch(WaitAction.waitStop())
                        dispatch(push(PATH_REFERENCIAL_PARAMETER))
                    })
                },
                save: () => {
                    checkMandatoryFields(mandatoryFileds, group, () => {
                        if ((checkedClassGroup && !classParameter.length) || (!checkedClassGroup && !usageParameter.length)) {
                            dispatch(ToastrAction.warning(i18n.pleaseEnterAtLeastOneParameter))
                        } else {
                            dispatch(WaitAction.waitStart())
                            dispatch(ParameterAction.updateParameterGroup({
                                parameterGroup: { ...group, status: hasValue(group.status) && `${group.status}` },
                                classParameterIds: classParameter.map(({ id }) => id),
                                usageParameterIds: usageParameter.map(({ id }) => id),
                            })).then(() => dispatch(WaitAction.waitStop()))
                            setEditMode(false)
                        }
                    })
                },
                cancel: () => {
                    setGroup(defaultParameterGroup)
                    setGroupParameters()
                    setEditMode(false)
                },
            }
        }
        return {
            edit: () => {
                setEditMode(true)
            },
        }
    }, [code, classParameter, usageParameter, group, isNew, editMode, defaultParameterGroup, checkedClassGroup])

    useEffect(() => {
        setGroupParameters()
    }, [parameters, parameterGroupLinks, code])

    const onCloseClassGroupDialog = () => {
        setSelectClassParameter(false)
    }

    const onCloseUsageGroupDialog = () => {
        setSelectUsageParameter(false)
    }

    const userEmail = contacts.find(c => c.id === accountUser?.contactCode)?.email
    const displayDev = userEmail?.includes('@aquasys.fr')

    const onCheckboxChange = () => {
        setIsClassGroup(prev => !prev)

        if (classParameter.length) {
            setUsageParameter(classParameter)
            setTempUsageParameter(tempClassParameter)
            setClassParameter([])
            setTempClassParameter([])
        }
        if (usageParameter.length) {
            setClassParameter(usageParameter)
            setTempClassParameter(tempUsageParameter)
            setUsageParameter([])
            setTempUsageParameter([])
        }
    }

    return isLoaded ? (
        <Stack spacing={1} margin='10px 10px 10px 0'>
            <Card>
                <CardTitle title={group.name} />
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <Input
                                title={i18n.nameGroup}
                                value={group.name}
                                disabled={!editMode}
                                onChange={value => setGroup({ ...group, name: value })}
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={value => setGroup({ ...group, status: value })}
                                value={group.status}
                                disabled={!editMode}
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                        <Grid size={4} sx={{ paddingTop: '15px' }}>
                            <Checkbox
                                label={i18n.classGroup}
                                checked={checkedClassGroup}
                                onChange={onCheckboxChange}
                                disabled={!isNew}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Textarea
                                value={group.description}
                                title={i18n.description}
                                onChange={value => setGroup({ ...group, description: value })}
                                disabled={!editMode}
                                height='15vh'
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {(checkedClassGroup || !!classParameter.length) && (
                <CardTable
                    title={i18n.classGroupParameters}
                    rows={classParameter}
                    headers={displayDev ? PARAMETER_GROUP_HEADERS_LINKS : PARAMETER_GROUP_HEADERS}
                    onClickRow={(e) => dispatch(push(`/referencial/parameter/${e.id}/dashboard`))}
                    actions={[{
                        icon: 'edit',
                        color: 'black',
                        onClick: () => setSelectClassParameter(true),
                        displayed: editMode,
                    }]}
                />
            )}
            {(!checkedClassGroup || !!usageParameter.length) && (
                <CardTable
                    title={i18n.usageGroupParameters}
                    rows={usageParameter}
                    headers={displayDev ? PARAMETER_GROUP_HEADERS_LINKS : PARAMETER_GROUP_HEADERS}
                    onClickRow={(e) => dispatch(push(`/referencial/parameter/${e.id}/dashboard`))}
                    actions={[
                        {
                            icon: 'edit',
                            color: 'black',
                            onClick: () => setSelectUsageParameter(true),
                            displayed: !checkedClassGroup && editMode,
                        },
                        {
                            icon: 'warningAmber',
                            displayed: checkedClassGroup,
                        },
                    ]}
                />
            )}
            <Dialog
                maxWidth='xl'
                fullWidth
                open={selectClassParameter}
            >
                <DefaultDialogTitle
                    title={i18n.classGroup}
                    onClose={onCloseClassGroupDialog}
                />
                <DialogContent>
                    <div style={{ height: '70vh' }}>
                        <SelectParameter
                            selectedParameters={tempClassParameter}
                            onChange={({ selectedParameters }) => setTempClassParameter(selectedParameters)}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const classParameters = parameters.filter(p => tempClassParameter.some(cp => cp.parameterCode === p.code)).map(p => ({ ...p, id: p.code }))
                            setClassParameter(classParameters)
                            setSelectClassParameter(false)
                        }}
                        variant='contained'
                        color='primary'
                    >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth='xl'
                fullWidth
                open={selectUsageParameter}
            >
                <DefaultDialogTitle
                    title={i18n.usageGroup}
                    onClose={onCloseUsageGroupDialog}
                />
                <DialogContent>
                    <div style={{ height: '70vh' }}>
                        <SelectParameter
                            selectedParameters={tempUsageParameter}
                            onChange={({ selectedParameters }) => setTempUsageParameter(selectedParameters)}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const usageParameters = parameters.filter(p => tempUsageParameter.some(cp => cp.parameterCode === p.code)).map(p => ({ ...p, id: p.code }))
                            setUsageParameter(usageParameters)
                            setSelectUsageParameter(false)
                        }}
                        variant='contained'
                        color='primary'
                    >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    ) : <ProgressCard progress={progress} />
}

ParameterGroupApp.propTypes = {
    parameterGroupLinks: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupLinkDto)),
    parameterGroups: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupDto)),
    parameters: PropTypes.arrayOf(PropTypes.instanceOf(ParameterDto)),
    fetchParameterGroupLinks: PropTypes.func,
    fetchParameterGroups: PropTypes.func,
    forceFetch: PropTypes.func,
    fetchParameters: PropTypes.func,
}

export default ParameterGroupApp